<template>
  <div class="coverage-page">
    <section class="coverage-hero">
      <div class="label">
        <div>
          <span class="eyebrow">Catalog automation</span>
          <h4>Build Coverage &amp; Smart Onboarding</h4>
        </div>
        <button class="btn-refresh" @click="fetchAll()">Refresh</button>
      </div>
      <p class="intro">
        How many valid, varied, available PC builds the catalog can produce — and a ranked plan to
        close the gaps. The <strong>report</strong> and <strong>plan</strong> jobs are read-only.
        <strong>Smart Auto-Onboard</strong> defaults to a no-cost dry run; only an explicit
        <em>dispatch</em> runs AI searches and creates candidates. Jobs live under
        <router-link class="inline-link" to="/users/jobs">Scheduled Jobs</router-link>;
        results land in <router-link class="inline-link" to="/users/candidates">Onboard New Parts</router-link>.
      </p>
    </section>

    <div v-if="error" class="alert">{{ error }}</div>
    <div v-if="notice" class="notice">{{ notice }}</div>

    <!-- Live run progress -->
    <div v-if="trackedRun" class="job-progress-card" :class="{ 'is-running': isRunActive(trackedRun) }">
      <div class="job-progress-head">
        <div>
          <span class="eyebrow">{{ trackedJobLabel }}</span>
          <h5>
            Run #{{ runId(trackedRun) || 'pending' }}
            <span class="run-status" :class="'st-' + runStatus(trackedRun)">{{ prettyStatus(runStatus(trackedRun)) }}</span>
          </h5>
        </div>
        <router-link class="inline-link" :to="trackedRunLink">Open full log</router-link>
      </div>
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: runProgress(trackedRun) + '%' }"></div>
      </div>
      <div class="job-progress-meta">
        <span>{{ runDuration(trackedRun) }}</span>
        <span v-if="isRunActive(trackedRun)">{{ liveConnected ? 'Live updates active' : 'Waiting for live updates' }}</span>
        <span v-else>Last updated {{ fmtRelative(runEndedAt(trackedRun) || runStartedAt(trackedRun)) }}</span>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs-row">
      <div class="tabs">
        <button class="tab" :class="{ active: activeTab === 'coverage' }" @click="activeTab = 'coverage'">
          Coverage
          <span v-if="headline.keystones" class="tab-count tone-bad">{{ headline.keystones }}</span>
        </button>
        <button class="tab" :class="{ active: activeTab === 'plan' }" @click="activeTab = 'plan'">
          Plan <span class="tab-count">{{ directives.length }}</span>
        </button>
        <button class="tab" :class="{ active: activeTab === 'onboard' }" @click="activeTab = 'onboard'">
          Smart Onboard
        </button>
      </div>
    </div>

    <!-- ============ COVERAGE ============ -->
    <section v-show="activeTab === 'coverage'">
      <div class="tab-actions">
        <button class="gc-act primary" :disabled="!!runningJob" @click="runJob('report')">
          {{ runningJob === jobs.report ? 'Analyzing…' : 'Run report' }}
        </button>
        <span v-if="reportMeta" class="run-meta-line">{{ reportMeta }}</span>
      </div>

      <div v-if="!hasCoverage" class="empty-state">
        No coverage report yet. Run the analysis to map keystone breaks and variety gaps.
      </div>

      <template v-else>
        <div class="kpi-row">
          <div class="kpi-card">
            <span class="kpi-label">Platforms</span>
            <span class="kpi-value">{{ headline.sockets }}</span>
            <span class="kpi-sub">CPU sockets evaluated</span>
          </div>
          <div class="kpi-card">
            <span class="kpi-label">Build steps</span>
            <span class="kpi-value">{{ headline.steps }}</span>
            <span class="kpi-sub">part choices analyzed</span>
          </div>
          <div class="kpi-card tone-ok">
            <span class="kpi-label">Healthy</span>
            <span class="kpi-value">{{ headline.healthySteps }}</span>
            <span class="kpi-sub">steps with real choice</span>
          </div>
          <div class="kpi-card keystone" :class="{ 'is-loud': headline.keystones > 0 }">
            <span class="kpi-label">Keystone breaks</span>
            <span class="kpi-value">{{ headline.keystones }}</span>
            <span class="kpi-sub">platform-stranding gaps</span>
          </div>
          <div class="kpi-card tone-warn">
            <span class="kpi-label">Variety gaps</span>
            <span class="kpi-value">{{ headline.varietyGaps }}</span>
            <span class="kpi-sub">thin but workable</span>
          </div>
        </div>

        <p v-if="coverageSummaryText" class="summary-text">{{ coverageSummaryText }}</p>

        <!-- Price summary -->
        <div class="section-head">
          <span class="eyebrow">Organic price bands</span>
          <h5>Price summary</h5>
          <p class="section-note">
            Computed from live catalog prices. Bands split each part type into
            low&nbsp;≤&nbsp;cut&nbsp;1&nbsp;≤&nbsp;med&nbsp;≤&nbsp;cut&nbsp;2&nbsp;≤&nbsp;high.
          </p>
        </div>
        <div class="table-wrap">
          <table class="c-table">
            <thead>
              <tr>
                <th>Part type</th>
                <th class="num">Available</th>
                <th class="num">Priced</th>
                <th class="num">Min</th>
                <th class="num">Median</th>
                <th class="num">Avg</th>
                <th class="num">Max</th>
                <th>Band cutoffs</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in priceRows" :key="row.type">
                <td><span class="type-chip">{{ prettyType(row.type) }}</span></td>
                <td class="num">{{ row.available }}</td>
                <template v-if="row.count > 0">
                  <td class="num">{{ row.count }}</td>
                  <td class="num money">{{ money(row.min) }}</td>
                  <td class="num money">{{ money(row.median) }}</td>
                  <td class="num money">{{ money(row.avg) }}</td>
                  <td class="num money">{{ money(row.max) }}</td>
                  <td>
                    <span v-if="row.cuts && row.cuts.length >= 2" class="band-cuts">
                      <span class="band-pill low">low ≤ {{ money(row.cuts[0]) }}</span>
                      <span class="band-pill med">med ≤ {{ money(row.cuts[1]) }}</span>
                      <span class="band-pill high">high</span>
                    </span>
                    <span v-else class="muted-cell">too few to band</span>
                  </td>
                </template>
                <template v-else>
                  <td class="num muted-cell" colspan="6">no priced parts</td>
                </template>
              </tr>
              <tr v-if="!priceRows.length">
                <td colspan="8" class="empty">No price summary in this run.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Form factor pools -->
        <div v-if="formFactorPools.length" class="section-head">
          <span class="eyebrow">Case / board pools</span>
          <h5>Form factor coverage</h5>
        </div>
        <div v-if="formFactorPools.length" class="ff-grid">
          <div v-for="ff in formFactorPools" :key="ff.key" class="ff-tile" :class="{ orphan: ff.orphan }">
            <span class="ff-name">{{ ff.label }}</span>
            <span class="ff-count">{{ ff.count }} part{{ ff.count === 1 ? '' : 's' }}</span>
            <span class="ff-tag" :class="ff.orphan ? 'tone-bad' : 'tone-ok'">{{ ff.orphan ? 'orphan pool' : 'viable' }}</span>
          </div>
        </div>

        <!-- Deficits -->
        <div class="section-head">
          <span class="eyebrow">Where choice collapses</span>
          <h5>Coverage deficits</h5>
          <p class="section-note">
            Problem steps grouped by platform, keystone breaks first.
          </p>
        </div>

        <div v-if="!deficits.length" class="all-clear">
          <span class="all-clear-badge">All clear</span>
          Every analyzed step has at least one compatible option with enough variety.
        </div>

        <div v-else class="deficit-groups">
          <div v-for="group in deficitGroups" :key="group.socket" class="deficit-group">
            <div class="deficit-group-head">
              <span class="socket-name">{{ group.socket }}</span>
              <span class="group-counts">
                <span v-if="group.keystones" class="mini-pill tone-bad">{{ group.keystones }} keystone</span>
                <span v-if="group.varieties" class="mini-pill tone-warn">{{ group.varieties }} variety</span>
              </span>
            </div>
            <div class="deficit-list">
              <div v-for="(d, idx) in group.items" :key="group.socket + '-' + idx"
                   class="deficit-row" :class="'is-' + statusOf(d)">
                <span class="status-badge" :class="'badge-' + statusOf(d)">
                  {{ statusOf(d) === 'keystone' ? 'Keystone' : 'Variety' }}
                </span>
                <div class="deficit-body">
                  <div class="deficit-label">{{ deficitLabel(d) }}</div>
                  <div class="deficit-reason" :class="'reason-' + statusOf(d)">{{ deficitReason(d) }}</div>
                  <div class="deficit-meta">
                    <span class="meta-chip">{{ d.total != null ? d.total : (d.observed && d.observed.count) || 0 }} options</span>
                    <span v-if="observedBands(d)" class="meta-chip">bands {{ observedBands(d) }}</span>
                    <span v-if="brandShare(d) != null" class="meta-chip">top brand {{ brandShare(d) }}%</span>
                    <span v-if="observedColors(d)" class="meta-chip">{{ observedColors(d) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Segment viable / orphan -->
        <div v-if="segments.length" class="section-head collapsible" @click="segmentsOpen = !segmentsOpen">
          <div>
            <span class="eyebrow">Per platform</span>
            <h5>Segment breakdown <span class="seg-count">{{ segments.length }}</span></h5>
          </div>
          <span class="chev" :class="{ open: segmentsOpen }">›</span>
        </div>
        <div v-if="segments.length && segmentsOpen" class="table-wrap">
          <table class="c-table">
            <thead>
              <tr>
                <th>Socket</th>
                <th class="num">CPUs</th>
                <th class="num">Motherboards</th>
                <th class="num">Keystone breaks</th>
                <th class="num">Variety gaps</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="seg in segments" :key="seg.socket">
                <td><span class="type-chip">{{ seg.socket || '—' }}</span></td>
                <td class="num">{{ seg.cpuCount != null ? seg.cpuCount : '—' }}</td>
                <td class="num">{{ seg.mbCount != null ? seg.mbCount : '—' }}</td>
                <td class="num"><span class="count-pill tone-bad" :class="{ dim: !seg.keystones }">{{ seg.keystones || 0 }}</span></td>
                <td class="num"><span class="count-pill tone-warn" :class="{ dim: !seg.varietyGaps }">{{ seg.varietyGaps || 0 }}</span></td>
                <td>
                  <span v-if="segmentOrphan(seg)" class="seg-status orphan" title="CPUs stranded with no boards — retire, not onboard">Orphan · retire</span>
                  <span v-else class="seg-status viable">Viable</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </section>

    <!-- ============ PLAN ============ -->
    <section v-show="activeTab === 'plan'">
      <div class="tab-actions">
        <button class="gc-act primary" :disabled="!!runningJob" @click="runJob('plan')">
          {{ runningJob === jobs.plan ? 'Planning…' : 'Run plan' }}
        </button>
        <span v-if="planMeta" class="run-meta-line">{{ planMeta }}</span>
      </div>

      <div v-if="!hasPlan" class="empty-state">
        No plan yet. Run the plan job to rank directives that would close the coverage gaps.
        This is read-only — it never writes.
      </div>

      <template v-else>
        <p v-if="planSummaryText" class="summary-text">{{ planSummaryText }}</p>

        <div class="section-head">
          <span class="eyebrow">Ranked directives</span>
          <h5>Onboarding plan <span class="seg-count">{{ directives.length }}</span></h5>
          <p class="section-note">
            What to bring in, in priority order. Platform-keystone breaks first, then sub-path keystones,
            then variety. Read-only — nothing here acts until you dispatch from Smart Onboard.
          </p>
        </div>

        <div v-if="!directives.length" class="all-clear">
          <span class="all-clear-badge">Nothing to onboard</span>
          The plan found no directives — coverage is already healthy.
        </div>

        <div v-else class="directive-list">
          <div v-for="d in sortedDirectives" :key="d.id" class="directive-row" :class="'tier-' + tierKey(d)">
            <div class="directive-rank">
              <span class="tier-badge" :class="'tier-' + tierKey(d)">{{ tierLabel(d) }}</span>
              <span v-if="d.keystone" class="keystone-mark" title="Keystone — strands a build path">★</span>
            </div>
            <div class="directive-main">
              <div class="directive-title-row">
                <span class="directive-title">{{ directiveTitle(d) }}</span>
                <span v-if="directiveScope(d)" class="directive-scope">{{ directiveScope(d) }}</span>
              </div>
              <div class="constraint-chips">
                <span v-for="(chip, i) in constraintChips(d)" :key="i" class="con-chip" :class="chip.tone ? 'tone-' + chip.tone : ''">
                  <span class="con-k">{{ chip.label }}</span>{{ chip.value }}
                </span>
                <span v-if="!constraintChips(d).length" class="muted-cell">no constraints</span>
              </div>
              <div v-if="d.blocked_cpus" class="directive-blocked">
                Blocks {{ blockedCount(d) }} CPU{{ blockedCount(d) === 1 ? '' : 's' }} from building
              </div>
              <div v-if="d.reason" class="directive-reason">{{ d.reason }}</div>
              <div v-if="d.research_note" class="directive-note">
                <button class="note-toggle" @click="toggleDirective(d)">
                  {{ expanded[d.id] ? 'Hide research note' : 'Research note' }}
                  <span class="chev" :class="{ open: expanded[d.id] }">›</span>
                </button>
                <p v-if="expanded[d.id]" class="note-body">{{ d.research_note }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Retire list -->
        <div v-if="retire.length" class="section-head">
          <span class="eyebrow">Prune, don't grow</span>
          <h5>Retire <span class="seg-count">{{ retire.length }}</span></h5>
          <p class="section-note">Orphaned parts the plan recommends retiring rather than onboarding around.</p>
        </div>
        <div v-if="retire.length" class="table-wrap">
          <table class="c-table">
            <thead>
              <tr>
                <th>Part type</th>
                <th>Platform</th>
                <th class="num">Count</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in retire" :key="r.id || i">
                <td><span class="type-chip">{{ prettyType(r.part_type) }}</span></td>
                <td>{{ (r.segment && r.segment.socket) || '—' }}</td>
                <td class="num">{{ r.count != null ? r.count : '—' }}</td>
                <td class="reason-cell">{{ r.reason || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </section>

    <!-- ============ SMART ONBOARD ============ -->
    <section v-show="activeTab === 'onboard'">
      <div class="onboard-control">
        <div class="control-head">
          <span class="eyebrow">Smart auto-onboard</span>
          <h5>Run the plan</h5>
          <p class="section-note">
            <strong>Plan</strong> is a dry run — it ranks directives and shows what it would do, with no
            AI cost and no writes. <strong>Dispatch</strong> runs AI searches and creates candidates for the
            top directives. Start with Plan.
          </p>
        </div>

        <div class="control-grid">
          <div class="control-field">
            <label>Mode</label>
            <div class="mode-toggle">
              <button type="button" class="mode-btn" :class="{ active: control.mode === 'plan' }" @click="control.mode = 'plan'">
                Plan <small>dry run · safe</small>
              </button>
              <button type="button" class="mode-btn danger" :class="{ active: control.mode === 'dispatch' }" @click="control.mode = 'dispatch'">
                Dispatch <small>runs AI · acts</small>
              </button>
            </div>
          </div>

          <div class="control-field">
            <label>Max directives</label>
            <input class="qty-input" type="number" min="1" max="50" v-model.number="control.maxDirectives" />
            <span class="field-hint">Quota — only the top N directives are processed.</span>
          </div>

          <div class="control-field">
            <label>Run pipeline</label>
            <label class="opt-switch">
              <span class="opt-switch-text" :class="{ on: control.runPipeline }">{{ control.runPipeline ? 'On' : 'Off' }}</span>
              <input type="checkbox" v-model="control.runPipeline" />
              <span class="switch-track"></span>
            </label>
            <span class="field-hint">Also enrich/scrape new candidates after discovery.</span>
          </div>
        </div>

        <div class="control-foot">
          <button
            class="gc-act"
            :class="control.mode === 'dispatch' ? 'danger-solid' : 'primary'"
            :disabled="!!runningJob"
            @click="runOnboard">
            {{ onboardButtonLabel }}
          </button>
          <span v-if="control.mode === 'dispatch'" class="dispatch-warn">
            Dispatch creates real candidates and spends AI budget. You'll be asked to confirm.
          </span>
          <span v-else class="dispatch-safe">No writes, no cost — safe to run anytime.</span>
        </div>
      </div>

      <p v-if="onboardMetaLine" class="run-meta-line onboard-meta">{{ onboardMetaLine }}</p>

      <!-- Findings -->
      <template v-if="hasOnboardRecord">
        <div class="section-head">
          <span class="eyebrow">{{ onboardDispatched ? 'Dispatch results' : 'Planned run' }}</span>
          <h5>Directive outcomes <span class="seg-count">{{ onboardFindings.length }}</span></h5>
          <p v-if="onboardReportSummary" class="section-note">{{ onboardReportSummary }}</p>
        </div>

        <div class="findings-summary" v-if="decisionChips.length">
          <span v-for="chip in decisionChips" :key="chip.key" class="stat-chip" :class="'tone-' + chip.tone">
            <span class="chip-key">{{ chip.label }}</span>
            <span class="chip-val">{{ chip.value }}</span>
          </span>
        </div>

        <div v-if="!onboardFindings.length" class="empty-state">
          This run produced no directive outcomes.
        </div>

        <div v-else class="table-wrap">
          <table class="c-table">
            <thead>
              <tr>
                <th>Directive</th>
                <th class="num">Requested</th>
                <th class="num">Found</th>
                <th>Outcome</th>
                <th>Candidates</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(f, i) in onboardFindings" :key="i">
                <td>
                  <div class="finding-dir">{{ findingTitle(f) }}</div>
                  <div v-if="findingScope(f)" class="finding-scope">{{ findingScope(f) }}</div>
                </td>
                <td class="num">{{ f.requested != null ? f.requested : '—' }}</td>
                <td class="num">
                  <span class="found-pill" :class="foundClass(f)">{{ f.found != null ? f.found : 0 }}</span>
                </td>
                <td>
                  <span v-if="f.dispatched" class="seg-status viable">Dispatched</span>
                  <span v-else class="seg-status planned">Planned</span>
                </td>
                <td>
                  <router-link
                    v-if="candidateIds(f).length"
                    class="inline-link"
                    :to="candidatesLink(f)">
                    {{ candidateIds(f).length }} candidate{{ candidateIds(f).length === 1 ? '' : 's' }} →
                  </router-link>
                  <span v-else class="muted-cell">none</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <div v-else class="empty-state">
        No auto-onboard run yet. Run a Plan dry run above to preview directive outcomes.
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'
import { clearPageJobRun, loadPageJobRun, savePageJobRun } from '../../services/pageJobTracker'

const PAGE_JOB_KEY = 'build-coverage'
import realtime from '../../services/realtime'

const JOBS = {
  report: 'catalog-coverage-report',
  plan: 'catalog-coverage-plan',
  onboard: 'catalog-auto-onboard'
}
const JOB_LABELS = {
  'catalog-coverage-report': 'Catalog coverage report',
  'catalog-coverage-plan': 'Catalog coverage plan',
  'catalog-auto-onboard': 'Smart auto-onboard'
}
const PART_TYPES = ['cpu', 'motherboard', 'ram', 'storage', 'gpu', 'psu', 'case']
const TYPE_LABELS = {
  cpu: 'CPU', motherboard: 'Motherboard', ram: 'RAM', storage: 'Storage',
  gpu: 'GPU', psu: 'PSU', case: 'Case'
}
const TIER_RANK = { 'platform-keystone': 3, 'subpath-keystone': 2, variety: 1 }
const TIER_LABELS = { 'platform-keystone': 'Platform keystone', 'subpath-keystone': 'Sub-path keystone', variety: 'Variety' }

export default {
  name: 'BuildCoverage',
  data () {
    return {
      jobs: JOBS,
      jobsList: [],
      runs: { report: null, plan: null, onboard: null },
      activeTab: 'coverage',
      error: null,
      notice: null,
      loading: false,
      runningJob: '',
      trackedRun: null,
      trackedRunId: '',
      trackedJobKey: '',
      unsubscribeTrackedRun: null,
      unsubscribeLiveState: null,
      liveFallbackTimer: null,
      liveConnected: false,
      segmentsOpen: false,
      expanded: {},
      control: { mode: 'plan', maxDirectives: 5, runPipeline: false }
    }
  },
  computed: {
    // ---- stats sources ----
    reportStats () { return this.runStats(this.runs.report) },
    planStats () { return this.runStats(this.runs.plan) },
    onboardStats () { return this.runStats(this.runs.onboard) },
    // Coverage prefers the report run, but the plan run carries the same shape as a fallback.
    coverageStats () {
      return this.hasReport ? this.reportStats : this.planStats
    },
    hasReport () { return !!(this.runs.report && this.runs.report.stats) },
    hasPlan () { return !!(this.runs.plan && this.runs.plan.stats) },
    hasCoverage () { return Object.keys(this.coverageStats).length > 0 },
    hasOnboardRecord () { return !!(this.onboardRecord && Object.keys(this.onboardRecord).length) },

    headline () {
      const s = this.coverageStats
      return {
        sockets: this.numv(s.sockets),
        steps: this.numv(s.steps),
        healthySteps: this.numv(s.healthySteps),
        keystones: this.numv(s.keystones),
        varietyGaps: this.numv(s.varietyGaps)
      }
    },
    priceRows () {
      const price = this.coverageStats.priceSummary || {}
      const counts = this.coverageStats.counts || {}
      return PART_TYPES.map((type) => {
        const p = price[type] || {}
        return {
          type,
          available: counts[type] != null ? counts[type] : 0,
          count: this.numv(p.count),
          min: p.min, max: p.max, avg: p.avg, median: p.median,
          cuts: Array.isArray(p.cuts) ? p.cuts : []
        }
      })
    },
    formFactorPools () {
      const ff = this.coverageStats.formFactors
      if (!Array.isArray(ff)) return []
      return ff.map((entry, i) => {
        const e = entry || {}
        const labelBits = []
        if (e.socket) labelBits.push(e.socket)
        if (e.form_factor || e.formFactor) labelBits.push(e.form_factor || e.formFactor)
        const count = this.numv(e.count != null ? e.count : (e.viable != null ? e.viable : e.parts))
        const orphan = e.orphan === true || count === 0
        return {
          key: e.id || labelBits.join('-') || i,
          label: labelBits.join(' · ') || (e.form_factor || 'Pool'),
          count,
          orphan
        }
      })
    },
    deficits () { return Array.isArray(this.coverageStats.deficits) ? this.coverageStats.deficits : [] },
    deficitGroups () {
      const groups = {}
      const order = []
      this.deficits.forEach((d) => {
        const socket = (d.context && d.context.socket) || 'Unspecified platform'
        if (!groups[socket]) {
          groups[socket] = { socket, items: [], keystones: 0, varieties: 0 }
          order.push(socket)
        }
        groups[socket].items.push(d)
        if (this.statusOf(d) === 'keystone') groups[socket].keystones += 1
        else groups[socket].varieties += 1
      })
      order.forEach((s) => groups[s].items.sort((a, b) => this.statusRank(b) - this.statusRank(a)))
      return order.map((s) => groups[s]).sort((a, b) => (b.keystones - a.keystones) || (b.varieties - a.varieties))
    },
    segments () { return Array.isArray(this.coverageStats.segments) ? this.coverageStats.segments : [] },

    // ---- plan ----
    directives () { return Array.isArray(this.planStats.directives) ? this.planStats.directives : [] },
    sortedDirectives () {
      return this.directives.slice().sort((a, b) => {
        const tr = this.tierRank(b) - this.tierRank(a)
        if (tr) return tr
        return this.directiveScore(b) - this.directiveScore(a)
      })
    },
    retire () { return Array.isArray(this.planStats.retire) ? this.planStats.retire : [] },

    // ---- onboard ----
    onboardRecord () {
      const r = this.onboardStats.record
      return (r && typeof r === 'object') ? r : {}
    },
    onboardFindings () { return Array.isArray(this.onboardRecord.findings) ? this.onboardRecord.findings : [] },
    onboardDispatched () { return this.onboardFindings.some((f) => f && f.dispatched) },
    onboardReportSummary () { return this.scalarSummary(this.onboardRecord.report_summary) },
    decisionChips () {
      const d = this.onboardRecord.decisions || {}
      const chips = []
      if (Array.isArray(d.onboard)) chips.push({ key: 'on', label: 'Onboard directives', value: d.onboard.length, tone: 'good' })
      if (Array.isArray(d.retire)) chips.push({ key: 're', label: 'Retire directives', value: d.retire.length, tone: 'warn' })
      const found = this.onboardFindings.reduce((n, f) => n + (this.numv(f.found)), 0)
      if (this.onboardFindings.length) chips.push({ key: 'fd', label: 'Candidates found', value: found, tone: found ? 'good' : 'bad' })
      return chips
    },
    onboardButtonLabel () {
      if (this.runningJob === JOBS.onboard) return this.control.mode === 'dispatch' ? 'Dispatching…' : 'Planning…'
      return this.control.mode === 'dispatch'
        ? `Dispatch top ${this.control.maxDirectives}`
        : 'Run plan (dry run)'
    },

    // ---- meta lines ----
    reportMeta () { return this.runMetaLine(this.runs.report) },
    planMeta () { return this.runMetaLine(this.runs.plan) },
    onboardMetaLine () { return this.runMetaLine(this.runs.onboard) },
    coverageSummaryText () { return this.scalarSummary(this.coverageStats.summary) },
    planSummaryText () { return this.scalarSummary(this.planStats.summary) },
    trackedJobLabel () { return JOB_LABELS[this.trackedJobKey] || 'Job run' },
    trackedRunLink () {
      const id = this.runId(this.trackedRun)
      return id ? `/users/jobs?run=${id}` : '/users/jobs'
    }
  },
  mounted () {
    this.fetchAll()
    this.restoreTrackedRun()
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    authHeaders () {
      return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.usertoken}` }
    },
    async fetchAll () {
      this.loading = true
      try {
        const { data } = await axios.get(`${apiDomain}jobs?includeHidden=true`, { headers: this.authHeaders() })
        const list = (data && (data.jobs || data)) || []
        this.jobsList = Array.isArray(list) ? list : []
        await Promise.all([this.loadJobRun('report'), this.loadJobRun('plan'), this.loadJobRun('onboard')])
        const known = this.jobsList.some((j) => Object.values(JOBS).includes(j.key))
        this.notice = (!known && !this.hasCoverage)
          ? 'The build-coverage jobs are not registered yet. Once they have run, their output appears here.'
          : null
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.loading = false
      }
    },
    jobFor (slot) { return this.jobsList.find((j) => j.key === JOBS[slot]) || null },
    async loadJobRun (slot) {
      const job = this.jobFor(slot)
      let runId = job && job.lastRun ? this.runId(job.lastRun) : ''
      if (!runId) runId = await this.findRunIdForJob(JOBS[slot])
      if (!runId) { this.$set(this.runs, slot, null); return }
      try {
        const { data } = await axios.get(`${apiDomain}jobs/runs/${runId}`, { headers: this.authHeaders() })
        this.$set(this.runs, slot, (data && data.run) ? data.run : data)
      } catch (e) {
        // leave prior run in place on transient failure
      }
    },
    async findRunIdForJob (key) {
      try {
        const { data } = await axios.get(`${apiDomain}jobs/runs?limit=25`, { headers: this.authHeaders() })
        const runs = (data && (data.runs || data)) || []
        const match = Array.isArray(runs) ? runs.find((r) => (r.jobKey || r.job || r.key) === key) : null
        return match ? this.runId(match) : ''
      } catch (e) {
        return ''
      }
    },

    // ---- running jobs ----
    async runJob (slot, options) {
      const key = JOBS[slot]
      this.runningJob = key
      this.trackedJobKey = key
      this.trackedRun = { id: '', jobKey: key, status: 'queued', startedAt: new Date().toISOString(), stats: {}, runKey: key }
      this.persistTrackedRun()
      this.notice = null
      try {
        const requestBody = {
          options: Object.assign({}, options || {})
        }
        const { data } = await axios.post(`${apiDomain}jobs/${key}/run`,
          requestBody, { headers: this.authHeaders() })
        const run = (data && data.run) ? data.run : data
        if (run && this.runId(run)) {
          this.trackedRun = Object.assign({}, this.trackedRun, run, { runKey: key })
          this.trackedRunId = this.runId(run)
          this.persistTrackedRun()
        }
        this.notice = `${JOB_LABELS[key]} started. Tracking progress on this page.`
        this.error = null
        this.ensurePolling()
      } catch (e) {
        this.error = this.errMsg(e)
        if (e && e.response && e.response.status >= 400 && e.response.status < 500) {
          this.runningJob = ''
          this.trackedRun = null
          this.trackedRunId = ''
          clearPageJobRun(PAGE_JOB_KEY, 'primary')
        } else {
          this.ensurePolling()
        }
      }
    },
    runOnboard () {
      const mode = this.control.mode === 'dispatch' ? 'dispatch' : 'plan'
      const n = Math.max(1, Number(this.control.maxDirectives) || 1)
      if (mode === 'dispatch') {
        const ok = window.confirm(
          `This will run AI searches and create candidates for the top ${n} directive${n === 1 ? '' : 's'} — continue?`
        )
        if (!ok) return
      }
      this.runJob('onboard', {
        mode,
        maxDirectives: n,
        runPipeline: !!this.control.runPipeline
      })
    },
    ensurePolling () {
      this.stopPolling()
      this.liveConnected = realtime.isConnected()
      this.unsubscribeLiveState = realtime.onStateChange((state, connected, detail) => {
        this.liveConnected = !!connected
        if (connected && state === 'open') {
          if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
          this.liveFallbackTimer = null
          this.error = null
          if (this.trackedRunId) realtime.refresh(`jobs:${this.trackedRunId}`)
        } else {
          if (state === 'error') this.error = realtime.errorMessage(detail)
          this.scheduleFallbackPoll(500)
        }
      })
      if (this.trackedRunId) {
        const topic = `jobs:${this.trackedRunId}`
        this.unsubscribeTrackedRun = realtime.subscribe(topic, {
          'job:snapshot': this.handleTrackedRunEvent,
          'job:progress': this.handleTrackedRunProgress,
          'job:finished': this.handleTrackedRunEvent,
          'job:error': this.handleTrackedRunError
        })
        if (realtime.isConnected()) realtime.refresh(topic)
      }
      this.scheduleFallbackPoll(5000)
    },
    stopPolling () {
      if (this.unsubscribeTrackedRun) this.unsubscribeTrackedRun()
      if (this.unsubscribeLiveState) this.unsubscribeLiveState()
      if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
      this.unsubscribeTrackedRun = null
      this.unsubscribeLiveState = null
      this.liveFallbackTimer = null
    },
    scheduleFallbackPoll (delay) {
      if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
      this.liveFallbackTimer = null
      if (realtime.isConnected() || !this.trackedRun || !this.isRunActive(this.trackedRun)) return
      this.liveFallbackTimer = setTimeout(async () => {
        this.liveFallbackTimer = null
        if (realtime.isConnected()) return
        await this.refreshTrackedRun()
        if (!realtime.isConnected() && this.trackedRun && this.isRunActive(this.trackedRun)) {
          this.scheduleFallbackPoll(2500)
        }
      }, delay || 2500)
    },
    extractRun (payload) {
      if (!payload || typeof payload !== 'object') return null
      if (payload.run) return payload.run
      if (payload.jobRun) return payload.jobRun
      if (payload.job_run) return payload.job_run
      if (payload.data && (payload.data.run || payload.data.jobRun || payload.data.job_run)) {
        return payload.data.run || payload.data.jobRun || payload.data.job_run
      }
      if (payload.id || payload.runId || payload.run_id) return payload
      return null
    },
    handleTrackedRunEvent (payload) {
      const run = this.extractRun(payload)
      if (!run) return
      this.applyTrackedRun(run)
    },
    handleTrackedRunError (payload) {
      const data = payload || {}
      this.error = data.message || data.reason || realtime.errorMessage(data)
    },
    handleTrackedRunProgress (payload) {
      const data = payload || {}
      if (this.trackedRun && !this.isRunActive(this.trackedRun)) return
      const run = this.extractRun(data) || {
        id: data.runId || data.run_id || data.id || this.trackedRunId,
        jobKey: this.trackedJobKey,
        status: data.status || (this.trackedRun && this.trackedRun.status) || 'running',
        stats: data.stats || data.progressStats,
        progress: data.progress,
        percent: data.percent || data.progressPercent
      }
      this.applyTrackedRun(run)
    },
    applyTrackedRun (incoming) {
      const run = Object.assign({}, this.trackedRun || {}, incoming || {})
      if (!run) return
      this.trackedRun = run
      this.trackedRunId = this.runId(run)
      if (this.trackedRunId) this.mergeSlotRun(run)
      if (!this.isRunActive(run)) {
        this.runningJob = ''
        this.stopPolling()
      }
    },
    mergeSlotRun (run) {
      const key = this.runJobKey(run)
      Object.keys(JOBS).forEach((slot) => {
        if (JOBS[slot] === key) this.$set(this.runs, slot, run)
      })
    },
    persistTrackedRun () {
      if (!this.trackedRun) return
      savePageJobRun(PAGE_JOB_KEY, 'primary', Object.assign({}, this.trackedRun, {
        jobKey: this.trackedJobKey || (this.trackedRun && this.trackedRun.jobKey),
        runKey: this.runningJob || this.trackedRun.runKey || this.trackedJobKey
      }))
    },
    async restoreTrackedRun () {
      const saved = loadPageJobRun(PAGE_JOB_KEY, 'primary')
      if (!saved) return
      this.trackedRunId = saved.runId || ''
      this.trackedJobKey = saved.jobKey || ''
      this.trackedRun = Object.assign({ stats: {} }, saved, { id: saved.runId || '' })
      this.runningJob = this.isRunActive(this.trackedRun) ? (saved.runKey || saved.jobKey || '') : ''

      if (!saved.runId) {
        this.ensurePolling()
        await this.refreshTrackedRun()
        return
      }
      try {
        const { data } = await axios.get(`${apiDomain}jobs/runs/${saved.runId}`, { headers: this.authHeaders() })
        const run = (data && data.run) ? data.run : data
        if (run) {
          this.trackedRun = Object.assign({}, saved, run)
          this.trackedRunId = this.runId(run)
          this.runningJob = this.isRunActive(run) ? (saved.runKey || saved.jobKey || '') : ''
          this.persistTrackedRun()
          if (this.isRunActive(run)) this.ensurePolling()
        }
      } catch (e) {
        if (e && e.response && e.response.status === 404) {
          clearPageJobRun(PAGE_JOB_KEY, 'primary')
          this.trackedRun = null
          this.trackedRunId = ''
          this.trackedJobKey = ''
          this.runningJob = ''
        } else {
          this.ensurePolling()
        }
      }
    },
    async refreshTrackedRun () {
      try {
        let match = null
        if (this.trackedRunId) {
          const { data } = await axios.get(`${apiDomain}jobs/runs/${this.trackedRunId}`, { headers: this.authHeaders() })
          match = (data && data.run) ? data.run : data
        } else {
          const { data } = await axios.get(`${apiDomain}jobs/runs?limit=25`, { headers: this.authHeaders() })
          const runs = (data && (data.runs || data)) || []
          match = this.findTrackedRun(Array.isArray(runs) ? runs : [])
        }
        if (match) {
          this.trackedRun = match
          this.trackedRunId = this.runId(match)
          this.persistTrackedRun()
          if (!this.isRunActive(match)) {
            this.runningJob = ''
            this.stopPolling()
            await this.fetchAll()
          }
        }
      } catch (e) {
        if (e && e.response && e.response.status === 404) {
          clearPageJobRun(PAGE_JOB_KEY, 'primary')
          this.trackedRun = null
          this.trackedRunId = ''
          this.trackedJobKey = ''
          this.runningJob = ''
          this.stopPolling()
        }
        // Keep the restored card on transient failures.
      }
    },
    findTrackedRun (runs) {
      if (!runs.length) return null
      if (this.trackedRunId) {
        const exact = runs.find((r) => String(this.runId(r)) === String(this.trackedRunId))
        if (exact) return exact
      }
      const sameJob = runs.filter((r) => (r.jobKey || r.job || r.key) === this.trackedJobKey)
      return sameJob.find((r) => this.isRunActive(r)) || sameJob[0] || null
    },

    // ---- deficit helpers ----
    statusOf (d) { return String(d && d.status).toLowerCase() === 'keystone' ? 'keystone' : 'variety' },
    statusRank (d) { return this.statusOf(d) === 'keystone' ? 2 : 1 },
    deficitLabel (d) {
      const ctx = (d && d.context) || {}
      const parts = [this.prettyType(d && d.type)]
      if (ctx.socket) parts.push(ctx.socket)
      if (ctx.ram_gen) parts.push(String(ctx.ram_gen).toUpperCase())
      if (ctx.form_factor) parts.push(ctx.form_factor)
      return parts.filter(Boolean).join(' · ')
    },
    deficitReason (d) {
      if (this.statusOf(d) === 'keystone') return 'NO options (keystone break)'
      const failures = (d && d.observed && d.observed.failures) || []
      return failures.length ? failures.join(' · ') : 'Too little real choice'
    },
    observedBands (d) {
      const bands = (d && d.observed && d.observed.bands) || []
      return bands.length ? bands.join('/') : ''
    },
    observedColors (d) {
      const colors = (d && d.observed && d.observed.colors) || []
      if (!colors.length) return ''
      return colors.length === 1 ? '1 color' : `${colors.length} colors`
    },
    brandShare (d) {
      const v = d && d.observed && d.observed.maxBrandShare
      if (v == null || isNaN(Number(v))) return null
      const n = Number(v)
      return Math.round(n <= 1 ? n * 100 : n)
    },
    segmentOrphan (seg) {
      if (!seg) return false
      const cpu = this.numv(seg.cpuCount)
      const mb = this.numv(seg.mbCount)
      // CPUs with no boards (or boards with no CPUs) can't form a build — retire, not onboard.
      return (cpu > 0 && mb === 0) || (mb > 0 && cpu === 0)
    },

    // ---- directive helpers ----
    tierKey (d) {
      const t = String((d && d.priority && d.priority.tier) || '').toLowerCase()
      return TIER_RANK[t] ? t : 'variety'
    },
    tierRank (d) { return TIER_RANK[this.tierKey(d)] || 0 },
    tierLabel (d) { return TIER_LABELS[this.tierKey(d)] || 'Variety' },
    directiveScore (d) { return Number(d && d.priority && d.priority.score) || 0 },
    directiveTitle (d) {
      const count = (d && d.count != null) ? d.count : 1
      return `${count}× ${this.prettyType(d && d.part_type)}`
    },
    directiveScope (d) {
      const seg = (d && d.segment) || {}
      const bits = []
      if (seg.socket) bits.push(seg.socket)
      if (seg.ram_gen) bits.push(String(seg.ram_gen).toUpperCase())
      if (seg.form_factor) bits.push(seg.form_factor)
      if (!bits.length && Array.isArray(d && d.sockets) && d.sockets.length) bits.push(d.sockets.join(', '))
      return bits.join(' · ')
    },
    blockedCount (d) {
      const b = d && d.blocked_cpus
      if (Array.isArray(b)) return b.length
      return this.numv(b)
    },
    constraintChips (d) {
      const c = (d && d.constraints) || {}
      const chips = []
      if (c.socket) chips.push({ label: 'socket', value: c.socket })
      if (c.ram_type) chips.push({ label: 'ram', value: String(c.ram_type).toUpperCase() })
      if (c.form_factor) chips.push({ label: 'form', value: c.form_factor })
      if (Array.isArray(c.price_bands) && c.price_bands.length) chips.push({ label: 'bands', value: c.price_bands.join('/') })
      if (c.price_hint && typeof c.price_hint === 'object') {
        const h = c.price_hint
        const v = [h.low, h.med, h.high].map((x) => this.money(x)).join(' / ')
        chips.push({ label: 'price', value: v })
      }
      if (Array.isArray(c.avoid_brands) && c.avoid_brands.length) chips.push({ label: 'avoid', value: c.avoid_brands.join(', '), tone: 'bad' })
      if (Array.isArray(c.need_colors_other_than) && c.need_colors_other_than.length) chips.push({ label: 'not color', value: c.need_colors_other_than.join(', '), tone: 'warn' })
      return chips
    },
    toggleDirective (d) {
      this.$set(this.expanded, d.id, !this.expanded[d.id])
    },

    // ---- finding helpers ----
    findingTitle (f) {
      const dir = (f && f.directive) || {}
      const pt = f.part_type || dir.part_type
      const count = f.requested != null ? f.requested : (dir.count != null ? dir.count : null)
      return `${count != null ? count + '× ' : ''}${this.prettyType(pt)}`
    },
    findingScope (f) {
      const dir = (f && f.directive) || {}
      return this.directiveScope(dir)
    },
    candidateIds (f) {
      return Array.isArray(f && f.candidateIds) ? f.candidateIds : []
    },
    candidatesLink (f) {
      const dir = (f && f.directive) || {}
      const pt = f.part_type || dir.part_type
      const q = pt ? `?type=${encodeURIComponent(pt)}` : ''
      return `/users/candidates${q}`
    },
    foundClass (f) {
      const found = this.numv(f && f.found)
      const requested = this.numv(f && f.requested)
      if (found === 0) return 'tone-bad'
      if (requested && found < requested) return 'tone-warn'
      return 'tone-ok'
    },

    // ---- formatting ----
    prettyType (type) {
      const key = String(type || '').toLowerCase()
      return TYPE_LABELS[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '—')
    },
    prettyStatus (status) {
      const s = String(status || '').toLowerCase()
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Pending'
    },
    money (n) {
      if (n == null || n === '' || isNaN(Number(n))) return '—'
      return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })
    },
    numv (v) {
      const n = Number(v)
      return (typeof v !== 'undefined' && v !== null && !isNaN(n)) ? n : 0
    },
    scalarSummary (v) {
      if (typeof v === 'string') return v
      if (v && typeof v === 'object' && typeof v.text === 'string') return v.text
      return ''
    },
    runStats (run) {
      return (run && run.stats && typeof run.stats === 'object') ? run.stats : {}
    },
    runMetaLine (run) {
      if (!run || !run.stats) return ''
      const when = this.runEndedAt(run) || this.runStartedAt(run)
      if (!when) return ''
      return `Run #${this.runId(run)} · ${this.prettyStatus(this.runStatus(run))} · ${this.fmtRelative(when)}`
    },

    // ---- run accessors ----
    runId (run) { return this.pick(run, ['id', '_id', 'runId'], '') },
    runStatus (run) { return this.pick(run, ['status', 'state'], 'queued') },
    runJobKey (run) { return this.pick(run, ['jobKey', 'job', 'key'], this.trackedJobKey || '') },
    runStartedAt (run) { return this.pick(run, ['startedAt', 'createdAt', 'queuedAt', 'startTime'], '') },
    runEndedAt (run) { return this.pick(run, ['endedAt', 'finishedAt', 'completedAt', 'endTime'], '') },
    isRunActive (run) {
      const status = String(this.runStatus(run)).toLowerCase()
      return ['starting', 'queued', 'pending', 'running', 'active'].indexOf(status) >= 0
    },
    runDuration (run) {
      const start = new Date(this.runStartedAt(run))
      if (isNaN(start)) return 'Waiting for run details'
      const endValue = this.runEndedAt(run)
      const end = endValue ? new Date(endValue) : new Date()
      if (isNaN(end)) return 'In progress'
      const seconds = Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000))
      if (seconds < 60) return `${seconds}s elapsed`
      return `${Math.floor(seconds / 60)}m ${seconds % 60}s elapsed`
    },
    runProgress (run) {
      if (!run) return 0
      const stats = this.pick(run, ['stats', 'progressStats'], {}) || {}
      const current = Number(this.pick(stats, ['current', 'processed', 'completed', 'done'], NaN))
      const total = Number(this.pick(stats, ['total', 'eligibleTotal'], NaN))
      const direct = Number(this.pick(run, ['progress', 'progressPercent', 'percent'], NaN))
      if (!isNaN(direct)) return Math.max(0, Math.min(100, Math.round(direct)))
      if (!isNaN(current) && !isNaN(total) && total > 0) return Math.max(4, Math.min(100, Math.round((current / total) * 100)))
      return this.isRunActive(run) ? 48 : 100
    },
    pick (obj, keys, fallback) {
      if (!obj) return fallback
      for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null) return obj[k]
      }
      return fallback
    },
    fmtRelative (t) {
      const d = new Date(t)
      if (isNaN(d)) return '—'
      const s = Math.round((Date.now() - d.getTime()) / 1000)
      if (s < 60) return `${s}s ago`
      const m = Math.floor(s / 60)
      if (m < 60) return `${m}m ago`
      const h = Math.floor(m / 60)
      if (h < 24) return `${h}h ago`
      return `${Math.floor(h / 24)}d ago`
    },
    errMsg (e) {
      if (e && e.response && e.response.data) {
        return e.response.data.message || e.response.data.error || `Request failed (${e.response.status})`
      }
      return (e && e.message) || 'Something went wrong'
    }
  }
}
</script>

<style scoped>
.coverage-page {
  padding: 4px 8px 48px;
  color: #cfd2da;
}

.label {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 1px solid rgba(67, 72, 87, 0.9);
}

.label h4 {
  margin: 0 0 6px;
  color: #d8dde8;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.intro {
  max-width: 880px;
  margin: 0.6rem 0 1.1rem;
  color: #a4acbc;
  font-size: 14px;
  line-height: 1.55;
}

.intro strong { color: #d8dde8; }

.inline-link { color: #1fb2e8; text-decoration: none; }
.inline-link:hover { text-decoration: underline; }

.alert,
.notice {
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.alert {
  border: 1px solid rgba(232, 79, 106, 0.5);
  background: rgba(90, 34, 48, 0.48);
  color: #f3c6cf;
}

.notice {
  border: 1px solid rgba(25, 151, 198, 0.5);
  background: rgba(20, 54, 70, 0.5);
  color: #aee0f3;
}

.empty-state {
  margin: 18px 0;
  padding: 28px;
  border: 1px dashed rgba(67, 72, 87, 0.9);
  border-radius: 10px;
  background: rgba(14, 18, 27, 0.55);
  color: #8f98aa;
  font-size: 14px;
  text-align: center;
}

.summary-text {
  margin: 14px 0 4px;
  color: #b8c0d0;
  font-size: 13.5px;
  line-height: 1.5;
}

/* Run progress */
.job-progress-card {
  margin: 12px 0 16px;
  padding: 13px 14px;
  border: 1px solid rgba(67, 72, 87, 0.86);
  border-radius: 8px;
  background:
    radial-gradient(circle at 18% 0%, rgba(25, 151, 198, 0.12), transparent 36%),
    rgba(10, 14, 22, 0.78);
}

.job-progress-card.is-running { border-color: rgba(25, 151, 198, 0.5); }

.job-progress-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.job-progress-head h5 {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 2px 0 0;
  color: #dce1eb;
  font-size: 15px;
  font-weight: 800;
}

.run-status {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 8px;
  border: 1px solid rgba(117, 124, 146, 0.34);
  border-radius: 999px;
  color: #aeb5c5;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.run-status.st-running,
.run-status.st-queued { border-color: rgba(25, 151, 198, 0.5); background: rgba(25, 151, 198, 0.12); color: #7bccee; }
.run-status.st-success { border-color: rgba(27, 201, 142, 0.42); background: rgba(27, 201, 142, 0.1); color: #89dfbd; }
.run-status.st-failed,
.run-status.st-error { border-color: rgba(232, 79, 106, 0.44); background: rgba(232, 79, 106, 0.12); color: #f0a1af; }

.progress-track {
  position: relative;
  height: 5px;
  margin-top: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(43, 51, 67, 0.78);
}

.progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, rgba(25, 151, 198, 0.78), rgba(27, 201, 142, 0.72));
  transition: width 0.45s ease;
}

.job-progress-card.is-running .progress-fill {
  background: linear-gradient(90deg, rgba(25, 151, 198, 0.42), rgba(27, 201, 142, 0.76), rgba(25, 151, 198, 0.42));
  background-size: 180% 100%;
  animation: progress-sheen 1.8s linear infinite;
}

@keyframes progress-sheen {
  from { background-position: 180% 0; }
  to { background-position: -180% 0; }
}

.job-progress-meta {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 8px;
  color: #8f98aa;
  font-size: 12px;
}

/* Tabs */
.tabs-row {
  margin: 22px 0 6px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
}

.tabs { display: flex; gap: 4px; }

.tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 16px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #9aa3b4;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
}

.tab:hover { color: #d7dce8; }
.tab.active { color: #1fb2e8; border-bottom-color: #1fb2e8; }

.tab-count {
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(58, 63, 77, 0.6);
  color: #aeb5c5;
  font-size: 10.5px;
  font-weight: 800;
}

.tab-count.tone-bad { background: rgba(232, 79, 106, 0.18); color: #f0a1af; }

.tab-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 16px 0 6px;
}

.run-meta-line { color: #767f92; font-size: 12px; }

/* KPIs */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin: 14px 0 8px;
}

.kpi-card {
  display: grid;
  gap: 5px;
  padding: 16px;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.08), transparent 38%),
    rgba(14, 18, 27, 0.78);
}

.kpi-label { color: #8f98aa; font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.kpi-value { color: #eaf0f7; font-size: 30px; font-weight: 800; line-height: 1; }
.kpi-sub { color: #767f92; font-size: 11px; }

.kpi-card.tone-ok { border-color: rgba(27, 201, 142, 0.34); }
.kpi-card.tone-ok .kpi-value { color: #89dfbd; }
.kpi-card.tone-warn { border-color: rgba(224, 192, 126, 0.36); }
.kpi-card.tone-warn .kpi-value { color: #e0c07e; }

.kpi-card.keystone { border-color: rgba(232, 79, 106, 0.4); }
.kpi-card.keystone .kpi-value { color: #f0a1af; }

.kpi-card.keystone.is-loud {
  border-color: rgba(232, 79, 106, 0.85);
  background:
    radial-gradient(circle at 12% 0%, rgba(232, 79, 106, 0.22), transparent 46%),
    rgba(30, 14, 19, 0.92);
  box-shadow: 0 0 0 1px rgba(232, 79, 106, 0.35), 0 8px 26px rgba(232, 79, 106, 0.18);
}

.kpi-card.keystone.is-loud .kpi-value { color: #ff8a9d; text-shadow: 0 0 18px rgba(232, 79, 106, 0.5); }
.kpi-card.keystone.is-loud .kpi-label { color: #f0a1af; }

/* Section heads */
.section-head { margin: 28px 0 12px; }

.section-head h5 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 0 0;
  color: #d8dde8;
  font-size: 15px;
  font-weight: 700;
}

.section-note {
  margin: 6px 0 0;
  max-width: 760px;
  color: #8f98aa;
  font-size: 12.5px;
  line-height: 1.5;
}

.section-head.collapsible {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  border-top: 1px solid rgba(67, 72, 87, 0.6);
  padding-top: 18px;
}

.section-head.collapsible .chev { color: #8f98aa; font-size: 22px; line-height: 1; transition: transform 0.18s ease; }
.section-head.collapsible .chev.open { transform: rotate(90deg); }

.seg-count {
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(58, 63, 77, 0.6);
  color: #aeb5c5;
  font-size: 10.5px;
  font-weight: 800;
}

/* Tables */
.table-wrap {
  overflow-x: auto;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background: rgba(9, 13, 20, 0.42);
}

.c-table { width: 100%; border-collapse: collapse; font-size: 13px; }

.c-table th {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
  color: #7f899b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.c-table td {
  padding: 11px 12px;
  border-bottom: 1px solid rgba(47, 51, 61, 0.84);
  color: #cfd2da;
  vertical-align: middle;
}

.c-table tbody tr:last-child td { border-bottom: 0; }
.c-table tbody tr:hover { background: rgba(25, 151, 198, 0.05); }
.c-table th.num, .c-table td.num { text-align: right; white-space: nowrap; }
.c-table td.money { font-family: monospace; color: #c9ced9; }
.reason-cell { color: #a4acbc; font-size: 12.5px; }

.muted-cell { color: #6f788b; font-style: italic; }

.type-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  min-width: 42px;
  padding: 0 8px;
  border: 1px solid rgba(25, 151, 198, 0.32);
  border-radius: 999px;
  background: rgba(25, 151, 198, 0.08);
  color: #7bccee;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.band-cuts { display: inline-flex; flex-wrap: wrap; gap: 5px; }

.band-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 999px;
  background: rgba(58, 63, 77, 0.3);
  color: #aeb5c5;
  font-size: 10.5px;
  font-weight: 700;
}

.band-pill.low { border-color: rgba(27, 201, 142, 0.3); color: #89dfbd; }
.band-pill.med { border-color: rgba(224, 192, 126, 0.32); color: #e0c07e; }
.band-pill.high { border-color: rgba(232, 79, 106, 0.3); color: #f0a1af; }

.mini-pill,
.count-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border: 1px solid rgba(117, 124, 146, 0.3);
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 800;
}

.count-pill { min-width: 26px; justify-content: center; }
.mini-pill.tone-bad, .count-pill.tone-bad { border-color: rgba(232, 79, 106, 0.4); background: rgba(232, 79, 106, 0.12); color: #f0a1af; }
.mini-pill.tone-warn, .count-pill.tone-warn { border-color: rgba(224, 192, 126, 0.36); background: rgba(224, 192, 126, 0.12); color: #e0c07e; }
.count-pill.dim { opacity: 0.4; }

/* Form factor pools */
.ff-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 10px;
}

.ff-tile {
  display: grid;
  gap: 4px;
  padding: 12px;
  border: 1px solid rgba(67, 72, 87, 0.86);
  border-radius: 8px;
  background: rgba(14, 18, 27, 0.6);
}

.ff-tile.orphan { border-color: rgba(232, 79, 106, 0.4); }
.ff-name { color: #dce1eb; font-size: 13px; font-weight: 700; }
.ff-count { color: #8f98aa; font-size: 12px; }
.ff-tag {
  justify-self: start;
  margin-top: 3px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}
.ff-tag.tone-ok { border: 1px solid rgba(27, 201, 142, 0.36); background: rgba(27, 201, 142, 0.1); color: #89dfbd; }
.ff-tag.tone-bad { border: 1px solid rgba(232, 79, 106, 0.4); background: rgba(232, 79, 106, 0.12); color: #f0a1af; }

/* All-clear */
.all-clear {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border: 1px solid rgba(27, 201, 142, 0.34);
  border-radius: 10px;
  background: rgba(16, 34, 28, 0.5);
  color: #a7d9c6;
  font-size: 13.5px;
}

.all-clear-badge {
  flex: none;
  padding: 3px 10px;
  border: 1px solid rgba(27, 201, 142, 0.42);
  border-radius: 999px;
  background: rgba(27, 201, 142, 0.12);
  color: #89dfbd;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

/* Deficits */
.deficit-groups { display: grid; gap: 16px; }

.deficit-group {
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 10px;
  background: rgba(11, 15, 23, 0.5);
  overflow: hidden;
}

.deficit-group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 14px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.7);
  background: rgba(20, 25, 35, 0.45);
}

.socket-name { color: #dce1eb; font-size: 13px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
.group-counts { display: flex; gap: 6px; }

.deficit-row {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(47, 51, 61, 0.7);
}

.deficit-row:last-child { border-bottom: 0; }
.deficit-row.is-keystone { background: linear-gradient(90deg, rgba(232, 79, 106, 0.08), transparent 60%); }

.status-badge {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 0;
  width: 100%;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.badge-keystone { border: 1px solid rgba(232, 79, 106, 0.55); background: rgba(232, 79, 106, 0.16); color: #f0a1af; }
.badge-variety { border: 1px solid rgba(224, 192, 126, 0.45); background: rgba(224, 192, 126, 0.12); color: #e0c07e; }

.deficit-body { min-width: 0; }
.deficit-label { color: #e7ebf3; font-size: 13.5px; font-weight: 700; }
.deficit-reason { margin-top: 3px; font-size: 12.5px; line-height: 1.4; }
.deficit-reason.reason-keystone { color: #f0a1af; font-weight: 700; }
.deficit-reason.reason-variety { color: #c8b07e; }
.deficit-meta { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }

.meta-chip {
  padding: 2px 8px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 6px;
  background: rgba(58, 63, 77, 0.3);
  color: #9aa3b4;
  font-size: 11px;
}

.empty { padding: 18px; color: #6f788b; text-align: center; }

/* Segment status */
.seg-status {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
}
.seg-status.viable { border: 1px solid rgba(27, 201, 142, 0.36); background: rgba(27, 201, 142, 0.1); color: #89dfbd; }
.seg-status.orphan { border: 1px solid rgba(232, 79, 106, 0.42); background: rgba(232, 79, 106, 0.12); color: #f0a1af; }
.seg-status.planned { border: 1px solid rgba(117, 124, 146, 0.34); background: rgba(58, 63, 77, 0.4); color: #aeb5c5; }

/* ---- Directives ---- */
.directive-list { display: grid; gap: 10px; }

.directive-row {
  display: grid;
  grid-template-columns: 132px minmax(0, 1fr);
  gap: 14px;
  padding: 14px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-left-width: 3px;
  border-radius: 10px;
  background: rgba(12, 16, 24, 0.55);
}

.directive-row.tier-platform-keystone { border-left-color: #e8536a; }
.directive-row.tier-subpath-keystone { border-left-color: #e8924f; }
.directive-row.tier-variety { border-left-color: #e0c07e; }

.directive-rank { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }

.tier-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-align: center;
  line-height: 1.2;
}

.tier-badge.tier-platform-keystone { border: 1px solid rgba(232, 83, 106, 0.55); background: rgba(232, 83, 106, 0.16); color: #f4889a; }
.tier-badge.tier-subpath-keystone { border: 1px solid rgba(232, 146, 79, 0.5); background: rgba(232, 146, 79, 0.14); color: #efb27e; }
.tier-badge.tier-variety { border: 1px solid rgba(224, 192, 126, 0.45); background: rgba(224, 192, 126, 0.12); color: #e0c07e; }

.keystone-mark { color: #f4889a; font-size: 14px; }

.directive-main { min-width: 0; }
.directive-title-row { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.directive-title { color: #eaf0f7; font-size: 15px; font-weight: 800; }
.directive-scope { color: #8f98aa; font-size: 12px; font-weight: 700; }

.constraint-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 9px; }

.con-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border: 1px solid rgba(67, 72, 87, 0.75);
  border-radius: 999px;
  background: rgba(20, 25, 35, 0.6);
  color: #c4ccd9;
  font-size: 11.5px;
}

.con-chip .con-k { color: #7f899b; font-size: 9.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; }
.con-chip.tone-bad { border-color: rgba(232, 79, 106, 0.4); color: #f0a1af; }
.con-chip.tone-bad .con-k { color: #cf8290; }
.con-chip.tone-warn { border-color: rgba(224, 192, 126, 0.4); color: #e0c07e; }
.con-chip.tone-warn .con-k { color: #c2a875; }

.directive-blocked {
  margin-top: 9px;
  color: #f0a1af;
  font-size: 12px;
  font-weight: 700;
}

.directive-reason { margin-top: 8px; color: #a4acbc; font-size: 12.5px; line-height: 1.45; }

.directive-note { margin-top: 8px; }

.note-toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #7bccee;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}

.note-toggle .chev { transition: transform 0.16s ease; }
.note-toggle .chev.open { transform: rotate(90deg); }

.note-body {
  margin: 8px 0 0;
  padding: 10px 12px;
  border-left: 2px solid rgba(25, 151, 198, 0.5);
  border-radius: 0 6px 6px 0;
  background: rgba(20, 25, 35, 0.5);
  color: #b8c0d0;
  font-size: 12.5px;
  line-height: 1.5;
  white-space: pre-wrap;
}

/* ---- Onboard control ---- */
.onboard-control {
  margin: 16px 0 4px;
  padding: 20px;
  border: 1px solid rgba(67, 72, 87, 0.86);
  border-radius: 12px;
  background:
    radial-gradient(circle at 14% 0%, rgba(25, 151, 198, 0.1), transparent 40%),
    rgba(12, 16, 24, 0.72);
}

.control-head h5 { margin: 2px 0 0; color: #d8dde8; font-size: 16px; font-weight: 700; }

.control-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
  margin: 20px 0 4px;
}

.control-field { display: grid; gap: 8px; align-content: start; }
.control-field > label { color: #8f98aa; font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.field-hint { color: #767f92; font-size: 11.5px; line-height: 1.4; }

.mode-toggle {
  display: inline-flex;
  padding: 3px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 9px;
  background: rgba(10, 13, 20, 0.6);
}

.mode-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  padding: 7px 14px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #9aa3b4;
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.2;
}

.mode-btn small { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; opacity: 0.8; }
.mode-btn.active { background: rgba(25, 151, 198, 0.16); color: #1fb2e8; box-shadow: inset 0 0 0 1px rgba(25, 151, 198, 0.45); }
.mode-btn.danger.active { background: rgba(232, 79, 106, 0.16); color: #f4889a; box-shadow: inset 0 0 0 1px rgba(232, 79, 106, 0.5); }

.qty-input {
  width: 100px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 7px;
  background: rgba(10, 13, 20, 0.56);
  color: #eaf0f7;
  font-size: 15px;
  font-weight: 700;
  outline: none;
}

.qty-input:focus { border-color: rgba(25, 151, 198, 0.85); box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12); }

.opt-switch {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin: 0;
  cursor: pointer;
}

.opt-switch-text { color: #8f98aa; font-size: 12.5px; font-weight: 800; }
.opt-switch-text.on { color: #1fb2e8; }
.opt-switch input { position: absolute; opacity: 0; pointer-events: none; }

.switch-track {
  position: relative;
  width: 35px;
  height: 19px;
  border: 1px solid rgba(117, 124, 146, 0.7);
  border-radius: 999px;
  background: rgba(10, 13, 20, 0.72);
  transition: border-color 0.18s ease, background 0.18s ease;
}

.switch-track::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #8f98aa;
  transition: transform 0.18s ease, background 0.18s ease;
}

.opt-switch input:checked + .switch-track { border-color: rgba(25, 151, 198, 0.88); background: rgba(25, 151, 198, 0.16); }
.opt-switch input:checked + .switch-track::after { transform: translateX(16px); background: #1fb2e8; }

.control-foot {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(67, 72, 87, 0.6);
}

.dispatch-warn { color: #cf8290; font-size: 12px; font-weight: 600; }
.dispatch-safe { color: #88a99a; font-size: 12px; }
.onboard-meta { margin-top: 14px; }

.findings-summary { display: flex; flex-wrap: wrap; gap: 8px; margin: 4px 0 14px; }

.stat-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 10px;
  border: 1px solid rgba(67, 72, 87, 0.74);
  border-radius: 999px;
  background: rgba(20, 25, 35, 0.58);
  font-size: 11.5px;
}

.stat-chip .chip-key { color: #8f98aa; font-weight: 700; }
.stat-chip .chip-val { color: #dce1eb; font-weight: 800; }
.stat-chip.tone-good .chip-val { color: #89dfbd; }
.stat-chip.tone-warn .chip-val { color: #e0c07e; }
.stat-chip.tone-bad .chip-val { color: #f0a1af; }

.finding-dir { color: #e7ebf3; font-weight: 700; }
.finding-scope { margin-top: 2px; color: #8f98aa; font-size: 11.5px; }

.found-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  padding: 1px 8px;
  border-radius: 999px;
  font-weight: 800;
}

.found-pill.tone-ok { border: 1px solid rgba(27, 201, 142, 0.36); background: rgba(27, 201, 142, 0.1); color: #89dfbd; }
.found-pill.tone-warn { border: 1px solid rgba(224, 192, 126, 0.36); background: rgba(224, 192, 126, 0.12); color: #e0c07e; }
.found-pill.tone-bad { border: 1px solid rgba(232, 79, 106, 0.4); background: rgba(232, 79, 106, 0.12); color: #f0a1af; }

/* Buttons */
.gc-act {
  height: 36px;
  padding: 0 16px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 7px;
  background: rgba(20, 25, 35, 0.7);
  color: #d7dce8;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 700;
  transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease;
}

.gc-act:hover:not(:disabled) { border-color: rgba(25, 151, 198, 0.7); background: rgba(25, 151, 198, 0.12); color: #eaf7fc; }
.gc-act.primary { border-color: rgba(25, 151, 198, 0.8); background: rgba(25, 151, 198, 0.14); color: #1fb2e8; }

.gc-act.danger-solid {
  border-color: rgba(232, 79, 106, 0.8);
  background: rgba(232, 79, 106, 0.16);
  color: #f4889a;
}

.gc-act.danger-solid:hover:not(:disabled) { border-color: #e8536a; background: rgba(232, 79, 106, 0.24); color: #ffd4dc; }
.gc-act:disabled { opacity: 0.5; cursor: default; }

.btn-refresh {
  min-width: 84px;
  height: 31px;
  margin-bottom: 8px;
  border: 1px solid rgba(25, 151, 198, 0.8);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.1);
  color: #1fb2e8;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  cursor: pointer;
}

.btn-refresh:hover { border-color: #1fb2e8; background: rgba(25, 151, 198, 0.2); color: #eaf7fc; }

@media (max-width: 1100px) {
  .kpi-row { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 720px) {
  .kpi-row { grid-template-columns: 1fr; }
  .deficit-row, .directive-row { grid-template-columns: 1fr; }
  .status-badge { width: auto; padding: 3px 10px; }
}
</style>
